"use client";

import * as React from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { dashboardNav } from "@/routes";
import {
  Menu,
  X,
  Sun,
  Moon,
  LogOut,
  Loader2,
  Star,
  UserIcon,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/providers/auth-provider";
import { useLobby } from "@/providers/lobby-provider";
import { LobbyNavIndicator } from "@/components/lobby/LobbyNavIndicator";
import { GolfIcon } from "@/components/icons/golf-icon";
import { useTheme } from "next-themes";
import { logout } from "@/lib/session";
import { useToast } from "@/hooks/use-toast";
import { MmrService } from "@/lib/domain/player/services/mmr.service";
import { MMR_CONSTANTS } from "@/types/match";
import { getProfileFromCache } from "@/lib/domain/player/services/user-profile-cache.service";

// Define interface for nav items
interface NavItem {
  href: string;
  label: string;
  icon: React.ReactElement;
  requiresAuth?: boolean;
}

const MobileNavItem = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentPropsWithoutRef<typeof Link> & {
    isActive?: boolean;
    isDisabled?: boolean;
  }
>(({ className, isActive, isDisabled, ...props }, ref) => {
  if (isDisabled) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-all opacity-50 cursor-not-allowed",
          className
        )}
      >
        {props.children}
      </div>
    );
  }

  return (
    <Link
      ref={ref}
      className={cn(
        "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-all active:scale-95",
        isActive
          ? "bg-accent text-accent-foreground"
          : "hover:bg-muted hover:text-foreground",
        className
      )}
      {...props}
    />
  );
});
MobileNavItem.displayName = "MobileNavItem";

const MobileNavbar = () => {
  const isMobile = useIsMobile();
  const pathname = usePathname();
  const router = useRouter();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const { user, isAnonymous } = useAuth();
  const { resolvedTheme, setTheme } = useTheme();
  const { toast } = useToast();
  const [userMmr, setUserMmr] = React.useState<number>(1000);
  const [displayName, setDisplayName] = React.useState<string>("");
  const [photoURL, setPhotoURL] = React.useState<string | null>(null);

  // Use dashboardNav for all navigation
  const navItems = dashboardNav;
  const bottomNavItems = dashboardNav.slice(0, 5);

  // Get active lobby state
  const { activeLobby } = useLobby();

  React.useEffect(() => {
    if (user) {
      fetchUserData();
    }
  }, [user]);

  const fetchUserData = async () => {
    if (!user) return;

    try {
      // Read from localStorage cache first (set by auth-provider or profile-settings)
      const cachedProfile = getProfileFromCache();
      if (cachedProfile && cachedProfile.id === user.id) {
        setDisplayName(cachedProfile.displayName || user.name || "Player");
        setPhotoURL(cachedProfile.photoURL);
      } else {
        // Fallback to Firebase Auth data
        setDisplayName(user.name || "Player");
        setPhotoURL(user.photoURL);
      }

      const mmrService = MmrService.getInstance();
      const mmr = await mmrService.getPlayerMmr(user.id);
      setUserMmr(mmr);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setUserMmr(MMR_CONSTANTS.INITIAL_MMR);
    }
  };

  const getMmrColor = (mmr: number) => {
    if (mmr >= 2000) return "text-blue-600 dark:text-blue-400";
    if (mmr >= 1500) return "text-green-600 dark:text-green-400";
    if (mmr >= 1000) return "text-yellow-600 dark:text-yellow-400";
    return "text-orange-600 dark:text-orange-400";
  };

  const toggleTheme = () => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  };

  const handleSignOut = async () => {
    setIsLoggingOut(true);
    try {
      const result = await logout();

      if (result.success) {
        toast({
          title: "Signed out",
          description: "You have been signed out successfully.",
        });
        setIsOpen(false);
        // Redirect to landing page
        router.push("/");
      } else {
        throw new Error(result.error || "Failed to sign out");
      }
    } catch (error) {
      console.error("Error signing out:", error);
      setIsLoggingOut(false);
      toast({
        title: "Error signing out",
        description: "There was a problem signing out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const PUBLIC_STANDALONE_ROUTES = [
    "/pricing",
    "/login",
    "/register",
    "/privacy",
    "/terms",
    "/forgot-password",
  ];

  if (!isMobile) return null;
  if (PUBLIC_STANDALONE_ROUTES.includes(pathname)) return null;

  return (
    <>
      {/* Mobile Top Navigation Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 h-14 md:hidden">
        <div className="flex items-center gap-2">
          <GolfIcon className="h-6 w-6" />
          <span className="text-xl font-bold">Local Links</span>
        </div>

        <div className="flex items-center gap-3">
          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="h-8 w-8"
          >
            {resolvedTheme === "dark" ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
            <span className="sr-only">Toggle theme</span>
          </Button>

          {/* Login/Sign Up Buttons (shown when not logged in) */}
          {!user && (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-8"
                onClick={() => router.push("/login")}
              >
                Login
              </Button>
              <Button
                size="sm"
                className="h-8"
                onClick={() => router.push("/register")}
              >
                Sign Up
              </Button>
            </div>
          )}

          {/* Create Account Button (shown for anonymous users) */}
          {user && isAnonymous && (
            <Button
              size="sm"
              className="h-8"
              onClick={() => router.push("/register")}
            >
              Create Account
            </Button>
          )}

          {/* Menu Button */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle Menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[75vw] max-w-xs p-0">
              <div className="flex flex-col h-full py-6 px-5">
                <div className="flex items-center justify-between pb-6 mb-2 border-b">
                  <div className="flex items-center gap-2">
                    <GolfIcon className="h-6 w-6" />
                    <span className="text-xl font-bold">Local Links</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setIsOpen(false)}
                  >
                    <X className="h-5 w-5" />
                    <span className="sr-only">Close</span>
                  </Button>
                </div>

                {/* User Info Section - matches desktop sidebar */}
                {user && (
                  <div className="px-1 pb-4 mb-2 border-b">
                    {isAnonymous ? (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-muted-foreground">
                          Guest User
                        </span>
                      </div>
                    ) : (
                      <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-3 border border-green-200 dark:border-green-800">
                        <div className="flex items-center gap-3">
                          {/* User Profile Photo */}
                          <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg shadow-md bg-green-100 dark:bg-green-900">
                            {photoURL ? (
                              <img
                                src={photoURL}
                                alt={displayName}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center bg-green-600">
                                <UserIcon className="h-5 w-5 text-white" />
                              </div>
                            )}
                          </div>
                          {/* User Name & MMR */}
                          <div className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                              {displayName}
                            </span>
                            <div
                              className={`flex items-center gap-1 ${getMmrColor(
                                userMmr
                              )}`}
                            >
                              <Star className="h-3.5 w-3.5" />
                              <span className="text-sm font-bold">
                                MMR: {userMmr}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex-1 overflow-auto">
                  <nav className="grid gap-1">
                    {navItems.map((item) => {
                      const isActive = pathname === item.href;
                      const isDisabled = false; // Add your own logic if needed
                      return (
                        <MobileNavItem
                          key={item.href}
                          href={item.href}
                          prefetch={false}
                          isActive={isActive}
                          isDisabled={isDisabled}
                          onClick={() => setIsOpen(false)}
                        >
                          {React.createElement(item.icon, {
                            className: "h-5 w-5",
                          })}
                          {item.label}
                        </MobileNavItem>
                      );
                    })}
                  </nav>
                </div>

                {user && (
                  <div className="pt-6 mt-2 border-t">
                    <Button
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={handleSignOut}
                      disabled={isLoggingOut}
                    >
                      {isLoggingOut ? (
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      ) : (
                        <LogOut className="mr-2 h-5 w-5" />
                      )}
                      {isLoggingOut ? "Signing out..." : "Sign Out"}
                    </Button>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Bottom Tab Navigation */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t flex items-center md:hidden"
        style={{ height: activeLobby ? "72px" : "56px" }}
      >
        <div className="grid grid-cols-5 w-full h-full">
          {bottomNavItems.map((item, index) => {
            // Check if this is the center item (index 2) and user has active lobby
            const isCenterItem = index === 2;
            const showLobbyIndicator = isCenterItem && activeLobby;

            if (showLobbyIndicator) {
              return (
                <div
                  key="lobby-indicator"
                  className="flex items-center justify-center relative"
                  style={{ marginTop: "-12px" }}
                >
                  <LobbyNavIndicator />
                </div>
              );
            }

            return (
              <Link
                key={item.href}
                href={item.href}
                prefetch={false}
                className={cn(
                  "flex flex-col items-center justify-center h-full transition-all active:scale-95",
                  pathname === item.href
                    ? "text-primary"
                    : "text-muted-foreground"
                )}
              >
                {React.createElement(item.icon, { className: "h-5 w-5" })}
                <span className="text-xs mt-1">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
};

export { MobileNavbar };
